import React, { useEffect } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { Button } from '../../components/base/Button';
import { updateSEO, seoData } from '../../utils/seo';

const HomePage: React.FC = () => {
  useEffect(() => {
    updateSEO(seoData.home);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-fixed pt-20"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 58, 138, 0.7), rgba(8, 145, 178, 0.7)), url('https://public.readdy.ai/ai/img_res/06a4c5890cff76542393a22610947303.jpg')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-cyan-600/60"></div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white py-12">
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-6 leading-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Building Africa's Most <strong>Reliable</strong> Hides and Skins Network
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl mb-8 max-w-3xl mx-auto font-light leading-relaxed">
            We connect rural suppliers in East and Southern Africa to ready buyers in Nigeria, 
            delivering premium air-dried cow hides with unmatched quality and reliability.
          </p>
          
          {/* Key Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 mb-12 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 sm:p-6">
              <div className="text-2xl sm:text-3xl font-bold text-cyan-300 mb-2">10+</div>
              <div className="text-base sm:text-lg">Containers Shipped Monthly</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 sm:p-6">
              <div className="text-2xl sm:text-3xl font-bold text-cyan-300 mb-2">100+</div>
              <div className="text-base sm:text-lg">Vendor Partners</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 sm:p-6">
              <div className="text-2xl sm:text-3xl font-bold text-cyan-300 mb-2">99%</div>
              <div className="text-base sm:text-lg">Quality Compliance</div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button href="/contact" variant="primary" size="lg">
              Contact Us
            </Button>
            <Button href="/suppliers" variant="secondary" size="lg">
              Become a Supplier
            </Button>
            <Button href="/buyers" variant="outline" size="lg">
              Buyer Inquiry
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Africa's Trusted Partner in <strong>Hides and Skins</strong>
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Henatos Limited is a Nigeria-headquartered trading and processing company sourcing, 
                grading, and importing air-dried cow hides from across East and Southern Africa. 
                Our hides are processed and sold in Nigeria as raw materials for Pomo Ijebu, 
                a popular local protein delicacy.
              </p>
              <p className="text-lg text-gray-700 mb-8">
                Founded before 2020 as a local wholesale trader, Henatos has scaled into a 
                pan-African enterprise with a structured sourcing network spanning Tanzania, 
                Burundi, Zambia, and Zimbabwe — ensuring consistency, transparency, and value 
                for both suppliers and buyers.
              </p>
              <Button href="/about" variant="primary">
                Learn More About Us
              </Button>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20African%20business%20team%20in%20modern%20office%20setting%2C%20diverse%20group%20of%20trade%20professionals%20reviewing%20quality%20documents%20and%20samples%2C%20clean%20corporate%20environment%20with%20maps%20of%20Africa%20on%20wall%2C%20natural%20lighting%2C%20business%20meeting%20atmosphere&width=600&height=400&seq=about1&orientation=landscape"
                alt="Henatos team and operations"
                className="rounded-lg shadow-xl object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Premium <strong>Air-Dried Cow Hides</strong>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specializing in premium air-dried cow hides averaging 6.5kg–12kg per piece, 
              sorted, graded, and baled for export with rigorous quality standards.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Quality Assurance</h3>
              <p className="text-gray-600">Strictly air-dried, unsalted hides inspected before shipment</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-scales-3-line text-2xl text-cyan-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Precise Grading</h3>
              <p className="text-gray-600">Professional sorting and grading by weight and quality</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-ship-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Export Logistics</h3>
              <p className="text-gray-600">Efficient shipping via Dar es Salaam, Beira, and Durban ports</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-2-line text-2xl text-cyan-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Vendor Support</h3>
              <p className="text-gray-600">Technical support on drying, storage, and quality standards</p>
            </div>
          </div>
        </div>
      </section>

      {/* Operations Map Section */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Pan-African <strong>Operations Network</strong>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our structured sourcing network spans across East and Southern Africa, 
              ensuring consistent supply and quality for our Nigerian buyers.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-blue-900 mb-2">Tanzania</h3>
                    <p className="text-gray-600">Primary sourcing hub with established vendor network and quality control facilities</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-cyan-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-blue-900 mb-2">Burundi & Rwanda</h3>
                    <p className="text-gray-600">Strategic partnerships for consistent supply of premium grade hides</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-blue-900 mb-2">Zambia & Zimbabwe</h3>
                    <p className="text-gray-600">Expanding operations with focus on quality and sustainable sourcing</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Map%20of%20Africa%20highlighting%20trade%20routes%20between%20East%20Africa%20and%20Nigeria%2C%20professional%20business%20illustration%20showing%20Tanzania%2C%20Burundi%2C%20Rwanda%2C%20Zambia%2C%20Zimbabwe%20connected%20to%20Nigeria%2C%20clean%20modern%20design%20with%20blue%20and%20cyan%20colors%2C%20logistics%20network%20visualization&width=600&height=500&seq=map1&orientation=landscape"
                alt="Henatos operations across Africa"
                className="rounded-lg shadow-xl object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Henatos */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Why Choose <strong>Henatos</strong>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our commitment to excellence sets us apart in the African commodities market
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-50 rounded-lg p-6 shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-award-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Proven Track Record</h3>
              <p className="text-gray-600">Years of successful operations with 99% quality compliance rate</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6 shadow-lg text-center">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-global-line text-2xl text-cyan-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Pan-African Network</h3>
              <p className="text-gray-600">Established operations across 5+ African countries</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6 shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Quality Assurance</h3>
              <p className="text-gray-600">Rigorous inspection and grading processes for every shipment</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6 shadow-lg text-center">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-2-line text-2xl text-cyan-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Dedicated Support</h3>
              <p className="text-gray-600">Comprehensive support for suppliers and buyers throughout the process</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-cyan-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Ready to Partner with <strong>Africa's Most Trusted</strong> Hides Network?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Whether you're a supplier looking for reliable partnerships or a buyer seeking 
            consistent quality, Henatos Limited is your trusted partner in African trade.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button href="/suppliers" variant="secondary" size="lg">
              Become a Supplier
            </Button>
            <Button href="/buyers" variant="outline" size="lg">
              Buyer Inquiry
            </Button>
            <Button href="/contact" variant="primary" size="lg">
              Contact Us Today
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
